import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Paper, CircularProgress, Box, Alert, TableRow,
    TableContainer, Table, TableBody, TableCell, Modal, IconButton, Tooltip,
    Accordion, AccordionSummary, AccordionDetails, Button, Dialog,
    DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Download as DownloadIcon,
    Description as DescriptionIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import HistorialAvanceTgu from './HistorialAvanceTgu';
// Nuevas librerías para previsualización
import { renderAsync as renderDocx } from 'docx-preview';
// Opcional: si usas webpack y quieres estilos por defecto
// import 'docx-preview/dist/docx-preview.css';
import * as XLSX from 'xlsx';

const MIME_EXT_MAP = {
  'application/pdf': '.pdf',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'text/plain': '.txt',
  'text/csv': '.csv',
  'application/json': '.json',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/zip': '.zip',
  'application/xml': '.xml'
};

const OFFICE_WORD_MIMES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]);

const OFFICE_EXCEL_MIMES = new Set([
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]);

// Convierte base64 a ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
  return bytes.buffer;
};

// Convierte base64 a Blob
const base64ToBlob = (base64, mime) => {
  const ab = base64ToArrayBuffer(base64);
  return new Blob([ab], { type: mime });
};

// Resuelve nombre de archivo con extensión basada en MIME si falta
const resolveFileName = (adjunto, mime) => {
  const fallback = `documento_${adjunto?.id ?? ''}`;
  const provided = (adjunto?.nombre_archivo || adjunto?.nombre || fallback).trim();
  const safe = provided.replace(/[\\/:*?"<>|]/g, '_');
  const hasExt = /\.[a-z0-9]{2,5}$/i.test(safe);
  const ext = MIME_EXT_MAP[(mime || '').toLowerCase()] || '';
  if (hasExt) return safe;
  return ext ? safe + ext : safe + '.bin';
};

const DetallesJefe = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tramite, setTramite] = useState(null);
    const [campos, setCampos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adjuntos, setAdjuntos] = useState([]);
    const [loadingAdjuntos, setLoadingAdjuntos] = useState(true);

    // Estados relacionados a la previsualización
    const [selectedFile, setSelectedFile] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [previewType, setPreviewType] = useState(null); // 'image' | 'pdf' | 'docx' | 'xlsx' | 'unsupported'
    const [objectUrl, setObjectUrl] = useState(null);     // Para imágenes/PDF
    const [previewHtml, setPreviewHtml] = useState('');   // Para Excel (HTML generado)
    const docxContainerRef = useRef(null);                // Contenedor para DOCX renderizado
    const [refreshHistorial, setRefreshHistorial] = useState(0);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [cerrando, setCerrrando] = useState(false);
    const [cerradoExitoso, setCerradoExitoso] = useState(false);
    const [areas, setAreas] = useState([]);
    const isCerrado = (tramite?.estado || '').toLowerCase() === 'cerrado';
    // Limpieza de recursos al cerrar modal o al desmontar
    const cleanupPreview = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
      }
      if (docxContainerRef.current) {
        docxContainerRef.current.innerHTML = '';
      }
      setPreviewHtml('');
      setPreviewType(null);
    };

    useEffect(() => {
      return () => cleanupPreview();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/por-sucursal/?sucursal_id=1`);
                if (!response.ok) throw new Error('No se pudo obtener los trámites');
                const data = await response.json();
                const tramiteFiltrado = data.find(item => item.id === parseInt(id));
                if (!tramiteFiltrado) throw new Error('No se encontró el trámite');

                setTramite(tramiteFiltrado);

                const tipoId = tramiteFiltrado.tipo;
                const camposRes = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/campos/${tipoId}/`);
                if (!camposRes.ok) throw new Error('No se pudieron obtener los campos del tipo de trámite');
                const camposData = await camposRes.json();
                setCampos(camposData.campos || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    useEffect(() => {
        const fetchAdjuntos = async () => {
            try {
                const res = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/adjuntos/listar/?entity_id=${id}`);
                const data = await res.json();
                setAdjuntos(data.adjuntos || []);
            } catch (err) {
                console.error('Error al obtener adjuntos:', err);
            } finally {
                setLoadingAdjuntos(false);
            }
        };

        if (id) fetchAdjuntos();
    }, [id]);

    useEffect(() => {
        const fetchAreas = async () => {
            try {
                const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/areas/');
                const data = await response.json();
                setAreas(data);
            } catch (err) {
                console.error('Error al cargar áreas:', err);
            }
        };
        fetchAreas();
    }, []);

    // Llama a la API y devuelve { base64, tipo_archivo }
    const fetchAdjunto = async (adjuntoId) => {
        const res = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/adjuntos/base64/?id=${adjuntoId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data?.base64 || !data?.tipo_archivo) throw new Error('Respuesta inválida: falta base64 o tipo_archivo');
        return data;
    };

    const handleViewFile = async (adjunto) => {
        try {
            cleanupPreview(); // limpiar previsualización anterior
            const { base64, tipo_archivo } = await fetchAdjunto(adjunto.id);
            const mime = (tipo_archivo || '').toLowerCase();

            // Imagen
            if (mime.startsWith('image/')) {
                const blob = base64ToBlob(base64, mime);
                const url = URL.createObjectURL(blob);
                setSelectedFile({ ...adjunto, mime, fileName: resolveFileName(adjunto, mime) });
                setObjectUrl(url);
                setPreviewType('image');
                setModalOpen(true);
                return;
            }

            // PDF
            if (mime === 'application/pdf') {
                const blob = base64ToBlob(base64, mime);
                const url = URL.createObjectURL(blob);
                setSelectedFile({ ...adjunto, mime, fileName: resolveFileName(adjunto, mime) });
                setObjectUrl(url);
                setPreviewType('pdf');
                setModalOpen(true);
                return;
            }

            // DOCX
            if (OFFICE_WORD_MIMES.has(mime)) {
                const ab = base64ToArrayBuffer(base64);
                setSelectedFile({ ...adjunto, mime, fileName: resolveFileName(adjunto, mime) });
                setPreviewType('docx');
                setModalOpen(true);
                // Renderizar dentro del contenedor del modal
                await renderDocx(ab, docxContainerRef.current);
                return;
            }

            // XLSX
            if (OFFICE_EXCEL_MIMES.has(mime)) {
                const ab = base64ToArrayBuffer(base64);
                const workbook = XLSX.read(ab, { type: 'array' });
                // Tomar la primera hoja por simplicidad
                const firstSheetName = workbook.SheetNames[0];
                const ws = workbook.Sheets[firstSheetName];
                // Generar HTML de la hoja
                const html = XLSX.utils.sheet_to_html(ws, { header: '', footer: '' });
                setSelectedFile({ ...adjunto, mime, fileName: resolveFileName(adjunto, mime) });
                setPreviewType('xlsx');
                setPreviewHtml(html);
                setModalOpen(true);
                return;
            }

            // Otros tipos: descargar directamente
            await handleDownloadFile(adjunto);
        } catch (err) {
            console.error('Error al obtener archivo base64:', err);
        }
    };

    const handleDownloadFile = async (adjunto) => {
        try {
            const { base64, tipo_archivo } = await fetchAdjunto(adjunto.id);
            const mime = (tipo_archivo || '').toLowerCase();
            const blob = base64ToBlob(base64, mime);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = resolveFileName(adjunto, mime);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedFile(null);
        cleanupPreview();
    };

    const handleCerrarTramite = async () => {
        setCerrrando(true);
        try {
            const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/cerrar/${id}/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error('Error al cerrar el trámite');

            await response.json();
            setCerradoExitoso(true);
            setTramite(prev => ({ ...prev, estado: 'Cerrado' }));
            setTimeout(() => navigate('/Legal/CrearCargaTGU'), 1000);
        } catch (err) {
            setError(err.message);
        } finally {
            setCerrrando(false);
            setConfirmDialogOpen(false);
        }
    };

    const handleOpenConfirmDialog = () => setConfirmDialogOpen(true);
    const handleCloseConfirmDialog = () => setConfirmDialogOpen(false);

    const normalizeFieldName = (fieldName) => {
        const lowerCaseField = fieldName.toLowerCase();
        const specialCases = {
            'fecha en que se reune el comite': 'fecha_comite',
            'fecha de asignacion': 'fecha_asignacion',
            'fecha creacion': 'fecha_creacion'
        };
        return specialCases[lowerCaseField] || lowerCaseField.replace(/ /g, '_');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                const [year, month, day] = dateString.split('-');
                return `${day}/${month}/${year}`;
            }
            const date = new Date(dateString);
            return date.toLocaleDateString('es-HN', { year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch {
            return dateString;
        }
    };

    const obtenerValorCampo = (campoNombre) => {
        const clave = normalizeFieldName(campoNombre);
        const valor = tramite[clave];
        if (clave === 'area' && typeof valor === 'number') {
            const areaEncontrada = areas.find(a => a.id === valor);
            return areaEncontrada ? areaEncontrada.nombre : 'Área no encontrada';
        }
        if (typeof campoNombre === 'string' && campoNombre.toLowerCase().includes('fecha')) {
            return formatDate(valor) || 'N/A';
        }
        return valor || 'N/A';
    };

    if (loading) {
        return (
            <Box textAlign="center" mt={4}>
                <CircularProgress />
                <Typography mt={2}>Cargando detalles...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 4 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container sx={{ mt: 4, width: '60%' }}>
            <Paper elevation={3} sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        Detalles del Trámite #{tramite?.id}
                    </Typography>
                </Box>

                {cerradoExitoso && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        El trámite se ha cerrado correctamente. Redirigiendo...
                    </Alert>
                )}

                <Box sx={{ mb: 2, backgroundColor: '#ffffff', padding: '16px', borderRadius: '8px' }}>
                    <TableContainer>
                        <Table>
                            <TableBody>
                                {campos.map((campo, index) => (
                                    <TableRow key={index}>
                                        <TableCell sx={{ fontWeight: 'bold', width: '50%', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                            {campo}
                                        </TableCell>
                                        <TableCell>
                                            {obtenerValorCampo(campo)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {loadingAdjuntos ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ padding: 0 }}>
                                        <Accordion elevation={0} sx={{ '&:before': { display: 'none' }, backgroundColor: 'transparent' }}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    borderTop: '1px solid #e0e0e0',
                                                    minHeight: '48px !important',
                                                    '& .MuiAccordionSummary-content': { margin: '12px 0' }
                                                }}
                                            >
                                                <Typography sx={{ fontWeight: 'bold' }}>
                                                    Documentos adjuntos ({adjuntos.length})
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ padding: 0, backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                                                <Box sx={{ p: 2 }}>
                                                    {adjuntos.length > 0 ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '300px', overflowY: 'auto' }}>
                                                            {adjuntos.map((adjunto) => (
                                                                <Paper
                                                                    key={adjunto.id}
                                                                    elevation={0}
                                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, '&:hover': { backgroundColor: '#f5f5f5' } }}
                                                                >
                                                                    <DescriptionIcon fontSize="small" color="action" />
                                                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                                                        {adjunto.nombre_archivo || ` ${adjunto.nombre}`}
                                                                    </Typography>
                                                                    <Tooltip title="Vista previa">
                                                                        <IconButton size="small" onClick={() => handleViewFile(adjunto)}>
                                                                            <VisibilityIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Descargar">
                                                                        <IconButton size="small" onClick={() => handleDownloadFile(adjunto)}>
                                                                            <DownloadIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Paper>
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                                                            No hay documentos adjuntos
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    </TableCell>
                                </TableRow>
                                   {isCerrado && (
                                    <>
                                        {/* Fila: Motivo de Cierre  */}
                                        <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', width: '50%', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                            MOTIVO DE CIERRE
                                        </TableCell>
                                        <TableCell>{tramite?.motivo_cierre || 'N/A'}</TableCell>
                                        </TableRow>
                                    </>
                                    )}
                            </TableBody>
                        </Table>
                    )}
                </Box>
                <HistorialAvanceTgu tramiteId={parseInt(id)} refreshTrigger={refreshHistorial} />
                
            </Paper>

            <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
                <DialogTitle>Confirmar cierre de trámite</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro que desea cerrar este trámite? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog} color="error" disabled={cerrando}>Cancelar</Button>
                    <Button onClick={handleCerrarTramite} color="success" disabled={cerrando} startIcon={cerrando ? <CircularProgress size={20} /> : <CheckCircleIcon />}>
                        {cerrando ? 'Cerrando...' : 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal para visualizar archivos */}
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Paper sx={{ width: '80%', maxWidth: '900px', maxHeight: '90vh', p: 3, position: 'relative', overflow: 'auto' }}>
                    <IconButton onClick={handleCloseModal} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h6" gutterBottom>
                        {selectedFile?.fileName || selectedFile?.nombre_archivo}
                    </Typography>

                    {selectedFile && (
                        <Box sx={{ mt: 2 }}>
                            {previewType === 'image' && objectUrl && (
                                <img src={objectUrl} alt={selectedFile?.fileName} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                            )}

                            {previewType === 'pdf' && objectUrl && (
                                <embed src={objectUrl} type="application/pdf" width="100%" height="700px" />
                            )}

                            {previewType === 'docx' && (
                                <div ref={docxContainerRef} style={{ maxHeight: '70vh', overflow: 'auto' }} />
                            )}

                            {previewType === 'xlsx' && (
                                <div
                                  style={{ maxHeight: '70vh', overflow: 'auto' }}
                                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                                />
                            )}

                            {!previewType && (
                                <Typography>
                                    Vista previa no disponible. <a onClick={() => handleDownloadFile(selectedFile)} style={{ cursor: 'pointer' }}>Descargar archivo</a>
                                </Typography>
                            )}
                        </Box>
                    )}
                </Paper>
            </Modal>
        </Container>
    );
};

export default DetallesJefe;
